import { Injectable } from '@angular/core';
import { IdBasedSubscriptionConfigService, ListLayout, ListLayoutMultipleComponent } from 'gung-list';
import {
  AuthService,
  CloudPimFiltersService,
  CurrentAssortmentService,
  GungFlowService,
  Product,
  ProductListConfigService,
  ProductService,
  ProductSortService
} from 'gung-standard';
import { BehaviorSubject, map, mergeMap } from 'rxjs';
import { CarLinerProductTableComponent } from '../components/car-liner-product-table/car-liner-product-table.component';
import { CarLinerProductGridViewComponent } from '../components/car-liner-product-grid-view/car-liner-product-grid-view.component';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CarLinerProductListByIdsAddonsConfigService
  extends ProductListConfigService
  implements IdBasedSubscriptionConfigService<Product>
{
  productIdsSubject = new BehaviorSubject<string[]>([]);
  showHeaderBanner = false;

  masterProduct: Product;
  isAddons: boolean;
  recommendedQuantities: { [productId: string]: number };

  constructor(
    protected productService: ProductService,
    protected authService: AuthService,
    protected productSortService: ProductSortService,
    protected gungFlowService: GungFlowService,
    protected cloudPimFiltersService: CloudPimFiltersService,
    protected currentAssortmentService: CurrentAssortmentService
  ) {
    super(
      productService,
      authService,
      productSortService,
      gungFlowService,
      cloudPimFiltersService,
      currentAssortmentService
    );
  }

  setSubscriptionIds(ids: string[]) {
    this.productIdsSubject.next(ids);
  }

  getItems() {
    return this.productIdsSubject.asObservable().pipe(
      mergeMap(productIds => this.productService.getProductsByIds(productIds)),
      map(products => {
        let filterProducts = products
          .filter(product => {
            // The following commented out lines should now be handled by flow settings:
            // if (product.extra.ar.itemtypecd3 != '1') {
            //   return false; // Exclude products that are not of type '1'
            // }
            // Only apply varugruppkod filtering for caroliner brand
            if (environment.projectName === 'caroliner' && product.extra && product.extra.ar && product.extra.ar.varugruppkod) {
              const varugruppkod = product.extra?.ar?.varugruppkod;
              // Codes that caroliner don't want to include.
              return (
                varugruppkod !== '7030' &&
                varugruppkod !== '7190' &&
                varugruppkod !== '8050' &&
                varugruppkod !== '6020' 
              );
            }
            return true;
          })
          .sort((a, b) => {
            if (Number(a.id) > Number(b.id)) {
              return 1;
            } else {
              return -1;
            }
          })
          .sort((a, b) => {
            // Helper function to get the part of id before "-"
            const extractId = id => {
              const parts = id.split('-');
              return parts[0]; // Take everything before "-"
            };

            const idA = extractId(a.id);
            const idB = extractId(b.id);

            // Compare the extracted parts as numbers
            return Number(idA) - Number(idB);
          });
        if (this.masterProduct?.extra?.artv && this.masterProduct?.extra?.artv.length > 0) {
          for (const product of filterProducts) {
            const addonCount = this.masterProduct.extra.artv.find(
              p => p.artnrtillvalart === product.id
            ).tillvalartantal;
            product.extra._addonPcs = Number(addonCount);
          }
        }
        if (this.recommendedQuantities) {
          for (const product of filterProducts) {
            if (this.recommendedQuantities[product.id]) {
              product.extra._recommendedQty = Number(this.recommendedQuantities[product.id]);
            }
          }
        }
        return filterProducts;
      }),
      map(products => {
        if (this.isAddons) {
          const artv = this.masterProduct.extra.artv
            .filter(r => r.q_ej_ecom !== '1')
            .sort((x, y) => Number(x.tillvalartprior) - Number(y.tillvalartprior)); // Sort by tillvalartprior

          products.sort((a, b) => {
            // Find the indices of matches in the sorted `artv` array
            const aIndex = artv.findIndex(r => r.artnrtillvalart === a.id);
            const bIndex = artv.findIndex(r => r.artnrtillvalart === b.id);

            // Sorting logic based on index in the sorted `artv` array
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex; // Respect the order in `artv`
            }
            if (aIndex !== -1) return -1; // `a` is found, but `b` is not
            if (bIndex !== -1) return 1; // `b` is found, but `a` is not
            return 0; // Neither `a` nor `b` match `artv`
          });
        }
        return products;
      })
    );
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fa fa-th',
        getListItemComponent: () => CarLinerProductGridViewComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductGrid'
      },
      {
        getIconClass: () => 'fa fa-list',
        getListItemComponent: () => CarLinerProductTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }
}
